'use client';

import { c as _c } from "react/compiler-runtime";
import { PostHog, PostHogProvider, usePostHog } from 'posthog-js/react';
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { fromPairsWithDuplicates } from '@repo/common/utils';
import { BullhornSessionContext } from '@/api/BullhornSession';
import posthogClient from 'posthog-js';
import { useSession } from '@/api/auth/client';
import { useDebounceSearchParams } from '@/hooks/hooks';
import { useCurrentUser } from '@/hooks/clientHooks';
import z from 'zod';
import { buildAnalyticsEventName, type EventParams, type AnalyticsEventCategory } from '@repo/common/analytics';
const gaId = process.env.GOOGLE_ANALYTICS_ID || '';
if (typeof window !== 'undefined') {
  const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (POSTHOG_KEY) {
    posthogClient.init(POSTHOG_KEY, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://app.posthog.com',
      ui_host: 'https://us.posthog.com',
      debug: process.env.NODE_ENV === 'development',
      session_recording: {
        maskAllInputs: false
      }
    });
  } else {
    console.warn('POSTHOG_KEY not set, disabling analytics');
  }
}
function WebVitals() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function PostHogClientWrapper(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <PostHogProvider client={posthogClient}>{children}</PostHogProvider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function usePostHogUser(posthog, userEmail, userDepartments) {
  const $ = _c(5);
  const [ready, setReady] = useState(false);
  const identifiedUserRef = useRef(null);
  const identifiedGroupRef = useRef(null);
  const identifiedDepartmentsRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== posthog || $[1] !== userDepartments || $[2] !== userEmail) {
    t0 = () => {
      if (!userEmail || posthog.has_opted_out_capturing()) {
        return;
      }
      if (identifiedUserRef.current !== userEmail) {
        posthog.identify(userEmail, {
          email: userEmail
        });
        identifiedUserRef.current = userEmail;
      }
      const departmentsKey = [...(userDepartments ?? [])].sort().join(",");
      if (userDepartments?.length && identifiedDepartmentsRef.current !== departmentsKey) {
        posthog.setPersonProperties({
          departments: userDepartments
        });
        identifiedDepartmentsRef.current = departmentsKey;
      }
      const domain = userEmail?.split("@")[1] ?? null;
      if (domain && identifiedGroupRef.current !== domain) {
        posthog.group("company", domain, {
          name: domain
        });
        identifiedGroupRef.current = domain;
      }
      setReady(true);
    };
    t1 = [posthog, userEmail, userDepartments];
    $[0] = posthog;
    $[1] = userDepartments;
    $[2] = userEmail;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return ready;
}
export function PostHogAnalytics() {
  const $ = _c(7);
  const posthog = usePostHog();
  const pathname = usePathname();
  const searchParams = useDebounceSearchParams();
  const {
    data: session
  } = useSession();
  const userEmail = session?.user?.email;
  const {
    data: me
  } = useCurrentUser();
  const isIdentified = usePostHogUser(posthog, userEmail, me?.departmentKey);
  let t0;
  let t1;
  if ($[0] !== isIdentified || $[1] !== pathname || $[2] !== posthog || $[3] !== searchParams || $[4] !== userEmail) {
    t0 = () => {
      if (!posthog.has_opted_out_capturing() && userEmail && isIdentified) {
        const url = buildFullUrl(pathname, searchParams);
        posthog.capture("app_pageview", {
          $current_url: url,
          $pathname: pathname,
          searchParams: fromPairsWithDuplicates([...searchParams.entries()])
        });
      }
    };
    t1 = [posthog, pathname, searchParams, userEmail, isIdentified];
    $[0] = isIdentified;
    $[1] = pathname;
    $[2] = posthog;
    $[3] = searchParams;
    $[4] = userEmail;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
  return null;
}
export function EmbeddedAnalytics() {
  const {
    session
  } = useContext(BullhornSessionContext);
  const posthog = usePostHog();
  const searchParams = useDebounceSearchParams();
  const pathname = usePathname();
  const userEmail = session?.userEmail;
  const isIdentified = usePostHogUser(posthog, userEmail, session?.departmentKey);
  useEffect(() => {
    try {
      if (!posthog.has_opted_out_capturing() && userEmail && isIdentified) {
        const url = buildFullUrl(pathname, searchParams);
        const payload = {
          $current_url: url,
          $pathname: pathname,
          searchParams: fromPairsWithDuplicates([...searchParams.entries()])
        };
        if (session) {
          console.log(payload);
          posthog.capture('embedded_pageview', {
            ...payload,
            has_session: true
          });
        } else {
          console.log('EmbeddedAnalytics: Session not ready yet');
          posthog.capture('embedded_pageview', {
            ...payload,
            has_session: false
          });
        }
      }
    } catch (err) {
      console.warn('EmbeddedAnalytics error', err);
    }
  }, [session, pathname, searchParams, posthog, userEmail, isIdentified]);
  return null;
}
export const Product = z.enum(['salesIq', 'talentIq', 'staffIq', 'dataIq', 'chrome_extension']);
export type Product = z.infer<typeof Product>;
type AnalyticsProps = {
  product?: Product;
} & Record<string, unknown>;
const AnalyticsPropsContext = createContext<AnalyticsProps>({});
export function AnalyticsProps(t0) {
  const $ = _c(6);
  const {
    children,
    props
  } = t0;
  const parentProps = useContext(AnalyticsPropsContext);
  let t1;
  if ($[0] !== parentProps || $[1] !== props) {
    t1 = {
      ...parentProps,
      ...props
    };
    $[0] = parentProps;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const combinedProps = t1;
  let t2;
  if ($[3] !== children || $[4] !== combinedProps) {
    t2 = <AnalyticsPropsContext.Provider value={combinedProps}>{children}</AnalyticsPropsContext.Provider>;
    $[3] = children;
    $[4] = combinedProps;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export type WrappedHog = {
  posthog: PostHog;
  parent?: WrappedHog;
  contextProps: AnalyticsProps;
  capture: PostHog['capture'];
};

/**
 * Hook that returns a posthog instance with all AnalyticsProps applied from the context
 */
export function useWrappedHog() {
  const posthog = usePostHog();
  const contextProps = useContext(AnalyticsPropsContext);
  return useWrappedHogInternal(posthog, contextProps);
}
function useWrappedHogInternal(parentInstance, props) {
  const $ = _c(11);
  const posthog = "posthog" in parentInstance ? parentInstance.posthog : parentInstance;
  const parentWrappedHog = "contextProps" in parentInstance ? parentInstance : undefined;
  const t0 = parentWrappedHog?.contextProps;
  let t1;
  if ($[0] !== props || $[1] !== t0) {
    t1 = {
      ...t0,
      ...props
    };
    $[0] = props;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const contextProps = t1;
  let t2;
  if ($[3] !== contextProps || $[4] !== posthog) {
    t2 = (eventName, eventProps, options) => posthog.capture(eventName, {
      ...contextProps,
      ...eventProps
    }, options);
    $[3] = contextProps;
    $[4] = posthog;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== contextProps || $[7] !== parentWrappedHog || $[8] !== posthog || $[9] !== t2) {
    t3 = {
      posthog,
      parent: parentWrappedHog,
      contextProps,
      capture: t2
    };
    $[6] = contextProps;
    $[7] = parentWrappedHog;
    $[8] = posthog;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}

/**
 * Hook that returns a posthog instance the given props applied as defaults
 */
export function useWrappedHogWithProps(props) {
  const posthog = useWrappedHog();
  return useWrappedHogInternal(posthog, props);
}
function buildFullUrl(pathname: string, searchParams: URLSearchParams): string {
  let url = window.origin + pathname;
  const queryString = searchParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }
  return url;
}
export function capturePosthogEvent<T extends AnalyticsEventCategory>(postHog: WrappedHog, params: EventParams<T>) {
  const eventName = buildAnalyticsEventName(params);
  postHog.capture(eventName, params.properties);
}
export function captureBullhornLinkClickEvent(posthog: WrappedHog, link?: string, options?: {
  properties?: Record<string, unknown>;
}) {
  capturePosthogEvent(posthog, {
    category: 'bullhorn',
    object: 'link',
    action: 'click',
    properties: {
      ...(link && {
        link_url: link
      }),
      ...options?.properties
    }
  });
}